import React, { useState, useEffect, useCallback } from 'react';
import { Input, InputGroup, InputGroupText } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useAuth } from 'hooks/useAuth';
import PageStructure from '../../components/Page-Structure';
import ShowComponent from '../../components/Show-Component';
import AccordionList from './components/Accordion-List';
import Shimmer from '../../components/Shimmer';
import { LoadingAccordions } from './components/Accordion-List/styles';
import { getAuth } from 'firebase/auth';
import axios from 'axios';

const TasksList = () => {
  const { t: translate } = useTranslation();
  const { user } = useAuth();
  const accountId = user.account;
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterValue, setFilterValue] = useState(null);

  const handleInputChange = (e) => {
    setFilterValue(e.target.value);
  };

  const processTasks = useCallback(
    (receivedTasks) => {
      if (!receivedTasks || receivedTasks.length === 0) {
        setTasks({});
        setLoading(false);
        return;
      }

      const newTaskState = {};

      const sortedTasks = Object.entries(receivedTasks)
        .sort((a, b) => a[1]['orderindex'] - b[1]['orderindex'])
        .map((entry) => entry[1]);

      for (const value of sortedTasks) {
        if (filterValue && !value.name.toLowerCase().includes(filterValue.toLowerCase())) {
          continue;
        }
        if (!newTaskState[value.status.status]) newTaskState[value.status.status] = [];
        newTaskState[value.status.status].push(value);
      }

      setTasks(newTaskState);
    },
    [filterValue, accountId],
  );

  const fetchTasks = useCallback(
    async (accountId) => {
      setLoading(true);

      try {
        const auth = getAuth();
        const user = auth.currentUser;
        await user.getIdToken().then((token) => {
          axios
            .get(`${process.env.REACT_APP_API_URL}/tasks?accountId=${accountId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
            .then(async (response) => {
              setStatuses(response.data.statuses);
              setAllTasks(response.data.tasks);
            })
            .catch((error) => {
              console.error('Error fetching tasks:', error);
            });
        });
      } catch (error) {
        console.error('Error fetching tasks:', error);
        setLoading(false);
      }
    },
    [accountId],
  );

  useEffect(() => {
    if (!accountId) return;

    fetchTasks(accountId);
  }, [accountId]);

  useEffect(() => {
    processTasks(allTasks);
  }, [filterValue, allTasks, processTasks]);

  return (
    <PageStructure metaTitle="Task List" breadcrumbItem="Task List" breadcrumbTitle="Tasks">
      <ShowComponent condition={loading}>
        <LoadingAccordions>
          <Shimmer height="3rem" />
          <Shimmer height="10rem" />
          <Shimmer height="10rem" />
          <Shimmer height="10rem" />
          <Shimmer height="10rem" />
          <Shimmer height="10rem" />
        </LoadingAccordions>
      </ShowComponent>
      <ShowComponent condition={!loading}>
        <div className="d-flex w-100 align-items-center justify-content-between mb-4 gap-3">
          <InputGroupStyled>
            <Input
              type="text"
              placeholder={translate('Search')}
              onChange={(e) => handleInputChange(e)}
            />
            <InputGroupText>
              <IconSearchStyled className="bx bx-search" />
            </InputGroupText>
          </InputGroupStyled>
        </div>
        {statuses.map((status) => (
          <AccordionList column={status.status} key={status.id} data={tasks?.[status.status]} />
        ))}
      </ShowComponent>
    </PageStructure>
  );
};

const InputGroupStyled = styled(InputGroup)`
  width: 32rem;

  input {
    border-right: none;
  }
  input:focus-within {
    border-color: #ced4da;
  }

  span {
    background-color: #ffffff;
    border-left: none;
  }
`;

const IconSearchStyled = styled.i`
  display: flex;
  font-size: 1.25rem;
  color: #718096;
`;

TasksList.displayName = 'Tasks List Page';

TasksList.propTypes = {};

export default TasksList;
